import React, { Suspense } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { DashboardLayout } from './DashboardLayout';
import { LoadingScreen } from '@/components/loading/LoadingScreen';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { GlobalErrorBoundary } from '@/components/ui/global-error-boundary';
import { isActiveStudent, isParent, hasAnyRole } from '@/utils/roleChecks';

interface ProtectedLayoutProps {
    requiredRole?: string | string[];
}

export const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ requiredRole }) => {
    const { currentUser, loading, isImpersonating } = useAuth();
    const location = useLocation();

    const { userSubscriptions } = useSubscriptions();

    // Don't block UI on subscription loading - allow background refresh
    // Only block on initial Auth load, but keep Layout structure if possible to prevent thrashing
    if (loading) {
        return (
            <DashboardLayout>
                <LoadingScreen message="Verifying session..." />
            </DashboardLayout>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // Check role if required - supports multi-role users
    if (requiredRole) {
        const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        // Use hasAnyRole helper for multi-role support, super_admin bypasses all checks
        const hasRole = hasAnyRole(currentUser, roles) || currentUser.role === 'super_admin';
        if (!hasRole) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    // Calculate Subscription Status
    let subscriptionState: 'active' | 'grace_period' | 'locked' = 'active';

    // Only apply logic for students and parents (not admins/coaches)
    // IMPORTANT: Skip subscription lock entirely during impersonation.
    // When an admin impersonates a user, the subscription hook is filtered by the admin's
    // selected branch, which may not match the impersonated user's subscription branchId,
    // causing a false 'locked' state even when the user has an active subscription.
    if (!isImpersonating && (isActiveStudent(currentUser) || isParent(currentUser))) {
        // Find most recent relevant subscription
        // We look for active ones first
        const activeSub = userSubscriptions.find(s => s.userId === currentUser.uid && s.status === 'active');

        if (activeSub) {
            const now = new Date();
            // Check date expiry
            if (activeSub.endDate < now) {
                // Expired. Check grace period.
                const diffMs = now.getTime() - activeSub.endDate.getTime();
                const diffDays = diffMs / (1000 * 60 * 60 * 24);

                if (diffDays <= 2) {
                    subscriptionState = 'grace_period';
                } else {
                    subscriptionState = 'locked';
                }
            }
            // Check class limit? 
            // If class limit reached, user is technically "out of credits", not "expired" in time.
            // Usually we just prevent booking. User didn't specify locking for credit exhaustion.
            // They specified "once plan expired it allows 2 days grace period... (month - subscription duration)"
        } else {
            // No active subscription at all?
            // If they have NO subscription, should they be locked?
            // Or maybe they are 'pending' (handled by DashboardLayout isPending check).
            // If they are 'active' user but no subscription (e.g. cancelled/expired long ago), they should probably be locked?
            // The requirement focuses on "when user subscribe... quota expires... grace period".
            // If they never subscribed, they are likely in "Onboarding".
            // If they had one and it's marked 'expired' in DB:
            const lastSub = userSubscriptions
                .filter(s => s.userId === currentUser.uid)
                .sort((a, b) => b.endDate.getTime() - a.endDate.getTime())[0];

            if (lastSub) {
                const now = new Date();
                const diffMs = now.getTime() - lastSub.endDate.getTime();
                const diffDays = diffMs / (1000 * 60 * 60 * 24);
                if (diffDays > 2) {
                    subscriptionState = 'locked';
                } else if (diffDays > 0) {
                    subscriptionState = 'grace_period';
                }
            }
            // If no sub ever, we assume Free/New Account? Or Onboarding?
            // Leaving as active for now to not break new users who haven't paid yet (OnboardingWizard handles them).
        }
    }

    // Exception: If user is accessing /subscriptions or /finance (to pay), we might want to allow it?
    // But DashboardLayout renders the overlay ON TOP of children.
    // If the overlay has a button that Navigates, we need to allow that Route to render WITHOUT the overlay?
    // DashboardLayout controls the overlay.
    // If we want to allow /subscriptions, we might need to handle it in DashboardLayout or change subscriptionState to 'active' on those routes.
    const isPaymentRoute = location.pathname.startsWith('/subscriptions') || location.pathname.startsWith('/finance') || location.pathname.startsWith('/wallet');
    if (isPaymentRoute && subscriptionState === 'locked') {
        // Technically we want the user to see the page, so we treat it as active or grace_period?
        // But if we just set it to active, they see the sidebar etc.
        // Maybe we keep 'locked' but DashboardLayout conditionally hides overlay on certain routes?
        // User said "shows exactly same screen... with blurred background".
        // The overlay button says "Renew Subscription". 
        // If clicking that goes to /subscriptions, we need to SHOW /subscriptions.
        // So on /subscriptions, we must NOT show the lock overlay.
        subscriptionState = 'active';
        // NOTE: This simple override allows access to payment pages.
    }

    return (
        <DashboardLayout subscriptionState={subscriptionState}>
            <Suspense fallback={<LoadingScreen message="Loading page..." />}>
                <GlobalErrorBoundary>
                    <Outlet />
                </GlobalErrorBoundary>
            </Suspense>
        </DashboardLayout>
    );
};
